<script setup lang="ts">
const content = ref('')
const isGenerating = ref(false)
const generatedSnippet = ref('')

const availableLanguages = ref(['English', 'Spanish (Español)'])
const selectedLanguage = ref('English')
const title = ref('')

const user = useUser()
const profile = useProfile()
const toast = useToast()

async function generate() {
    isGenerating.value = true

    try {
        generatedSnippet.value = await $fetch(user.value ? '/api/generate-snippet-pro' : '/api/generate-snippet', {
            method: 'POST',
            body: {
                content: content.value,
                language: selectedLanguage.value,
                title: title.value,
            },
        })

        if (profile.value) {
            profile.value.credits -= 1
        }
    } catch (error) {
        toast.add({
            title: 'An error occurred',
            description: 'Please try again later.',
            color: 'error',
        })
    } finally {
        isGenerating.value = false
    }
}

onMounted(async () => {
    profile.value = await $fetch('/api/profile')
})

const _title = 'EasySnippets: A Meta Description Generator'
const _description = 'A tool designed to create user-friendly meta descriptions that enhance SEO and provide accurate page summaries for search engine results.'

useSeoMeta({
    title: _title,
    description: _description,
    ogTitle: _title,
    twitterTitle: _title,
    ogDescription: _description,
    twitterDescription: _description,
})

const creatingOrder = ref(false)

async function createOrder() {
    creatingOrder.value = true

    try {
        const { url } = await $fetch<{ url: string }>('/api/create-order', {
            method: 'POST',
            body: {
                product_name: '500 generations',
            },
        })

        if (url) {
            window.location.href = url
        }
    } catch (error) {
        toast.add({
            title: 'An error occurred',
            description: 'Please try again later.',
            color: 'error',
        })
    } finally {
        creatingOrder.value = false
    }
}

const items = ref([
    {
        label: 'Log out',
        onSelect: async () => {
            const supabase = useSupabase()
            await supabase.auth.signOut()
        }
    }
])
</script>

<template>
    <div class="max-h-[100dvh] overflow-y-auto">
    <div class="fixed top-0 w-full p-6 flex justify-end gap-6">
        <div v-if="profile" class="flex gap-1 items-center">
            <UIcon name="solar:bolt-bold" />
            <span>{{ profile?.credits }} credits left</span>
        </div>
        <UDropdownMenu v-if="user" :items="items">
            <UButton color="neutral" variant="outline" icon="solar:user-bold" />
        </UDropdownMenu>
        <ContinueWithGoogle v-else />
    </div>
    <div class="min-h-[100dvh] bg-[var(--ui-bg-elevated)] px-6">
        <div class="max-w-2xl mx-auto py-12 flex flex-col gap-6 text-center">
            <span>Welcome to EasySnippets!</span>
            <h1 class="max-w-lg text-balance font-bold text-5xl leading-tight mx-auto">Generate Meta Descriptions with AI</h1>
            <p>
                EasySnippets is a tool that has been exclusively designed to help you generate <b>good descriptions</b> for your blog posts that doesn't look like they were written by AI like other tools. You'll get user-friendly snippets that are good for SEO.
            </p>
            <p>
                Meta descriptions are used by search engines to create a snippet for the search result of your page if they give users a more accurate description of the page than content taken directly from the page.
            </p>
            <div class="flex flex-col gap-6 items-end text-left">
                <UFormField class="w-full" label="Content" 
                :help="user ? 'Maximun 100k characters.' : 'Maximum 10k characters. Sign up for free and get 10 generations with up to 100k characters each.'">
                    <UTextarea
                        v-model="content"
                        class="w-full"
                        
                        placeholder="Paste here the content of your blog post..."
                        size="xl"
                    />
                </UFormField>
                <UFormField class="w-full" label="Title (optional)" help="You can include a title to avoid the snippet being redundant with the title.">
                    <UInput class="w-full" placeholder="Paste here the title of your blog post..." v-model="title" />
                </UFormField>
                <div class="flex gap-2">
                    <USelect
                        :items="availableLanguages"
                        v-model="selectedLanguage"
                        size="xl"
                    />
                    <UButton
                        @click="generate"
                        size="xl"
                        icon="solar:magic-stick-3-bold"
                        :loading="isGenerating"
                    >
                        Generate
                    </UButton>
                </div>
            </div>
            <span class="text-left">Your result</span>
            <Result :generated-snippet="generatedSnippet" />
            <template v-if="generatedSnippet">
                <UAlert
                    v-if="generatedSnippet.length < 85"
                    color="warning"
                    icon="solar:check-circle-bold"
                    :title="`Be careful, the snippet looks kinda short (${generatedSnippet.length} characters)`"
                    description="Maybe you should try again."
                />
                <UAlert
                    v-else-if="generatedSnippet.length > 170"
                    color="warning"
                    icon="solar:check-circle-bold"
                    :title="`Be careful, the snippet looks kinda long (${generatedSnippet.length} characters)`"
                    description="Even though technically there's no limit, maybe you should try again."
                />
                <UAlert
                    v-else-if="generatedSnippet.length > 200"
                    color="error"
                    icon="solar:check-circle-bold"
                    :title="`Your snippet came out too long (${generatedSnippet.length} characters)`"
                    description="Maybe you should try again."
                />
                <UAlert v-else
                    color="success"
                    icon="solar:check-circle-bold"
                    :title="`Your snippet looks good (${generatedSnippet.length} characters)`"
                    description="It has the right length and it's a good snippet for Google!"
                />
            </template>
            <h2 class="text-2xl font-bold mt-12">Pricing</h2>
            <div class="grid grid-cols-1 gap-4">
                <div v-if="!user" class="flex flex-col gap-2 border border-[var(--ui-border)] p-4 rounded-[calc(var(--ui-radius)*2)]">
                    <span class="text-lg font-bold">Free</span>
                    <p class="text-sm">Sign up for free and get 10 generations.</p>
                    <p class="text-sm">No surveys, no extra steps. Just sign up with Google and you can start using the product in seconds. And don't worry, we don't recopilate any sensitive information.</p>
                    <ContinueWithGoogle class="w-fit mx-auto" />
                </div>
                <div class="flex flex-col gap-2 border border-[var(--ui-border)] p-4 rounded-[calc(var(--ui-radius)*2)]">
                    <span class="text-lg font-bold">500 credits</span>
                    <p class="text-sm">Get 500 generations for US$5.</p>
                    <p class="text-sm">Use them whenever you want. No subscriptions.</p>
                    <p class="text-sm">API coming soon.</p>
                    <template v-if="!user">
                        <ContinueWithGoogle class="w-fit mx-auto" />
                        <span class="text-xs">and then</span>
                    </template>
                    <UButton icon="solar:card-bold" :disabled="!user" color="primary" class="w-fit mx-auto" @click="createOrder">Purchase for US$5</UButton>
                </div>
            </div>
            <p class="text-sm text-[var(--ui-text-dimmed)]">EasySnippets &copy; {{ new Date().getFullYear() }}</p>
        </div>
    </div>
</div>
</template>